import { useState, useCallback, useRef } from "react";
import Papa from "papaparse";
import { Upload, Download, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { importPropFirmHistory, useAction, getAccountsByUser, useQuery, getPropFirmTemplates, getTradingStrategiesByUser } from "wasp/client/operations";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../client/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../client/components/ui/accordion";
import { Button } from "../client/components/ui/button";
import { toast } from "../client/hooks/use-toast";
import useTranslation from "../client/hooks/useTranslation";

// ── Constants ──────────────────────────────────────────────────────────────

import {
  COL,
  MAX_PAYOUTS,
  payoutDateCol,
  payoutAmountCol,
  upgradeLegacyRows,
} from "./importColumns";
import { detectWrongCsvFormat } from "./detectWrongCsv";

// ── Column letter helper (0→A, 25→Z, 26→AA …) ─────────────────────────────

function colLetter(idx: number): string {
  let result = "";
  let n = idx;
  while (n >= 0) {
    result = String.fromCharCode((n % 26) + 65) + result;
    n = Math.floor(n / 26) - 1;
  }
  return result;
}

// ── Types ──────────────────────────────────────────────────────────────────

interface ParsedPayout { date: string; montant: number; }

interface ParsedAccount {
  rowNum: number;
  propfirm: string;
  nom_compte: string;
  tiers: string;
  taille_compte: number;
  prix_usd: number;
  groupe?: string;
  strategie?: string;
  frais_activation?: number;
  date_achat?: string;
  date_cloture?: string;
  payouts: ParsedPayout[];
}

type ConflictResolution = "skip" | "update";

interface ConflictEntry {
  account: ParsedAccount;
  resolution: ConflictResolution;
}

interface ParseError {
  cell: string;
  message: string;
}

interface ParseResult {
  toCreate: ParsedAccount[];
  conflicts: ConflictEntry[];
  errors: ParseError[];
  /** Le fichier n'est pas un export de comptes prop firm (mauvais formulaire).
   *  On affiche alors une explication à la place des erreurs de cellules, qui
   *  seraient exactes mais incompréhensibles. */
  wrongFormat?: boolean;
}

// ── Validators ─────────────────────────────────────────────────────────────

function isValidDate(v: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(v.trim()) && !isNaN(Date.parse(v.trim()));
}

function isPositiveNumber(v: string): boolean {
  const n = parseFloat(v);
  return isFinite(n) && n > 0;
}

function looksLikeDate(v: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(v.trim()) || /^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}$/.test(v.trim());
}

// ── Parser ─────────────────────────────────────────────────────────────────

function parseRows(raw: string[][], existingNames: Set<string>): ParseResult {
  const toCreate: ParsedAccount[] = [];
  const conflicts: ConflictEntry[] = [];
  const errors: ParseError[] = [];
  const seenNames = new Map<string, number>();

  for (let idx = 0; idx < raw.length; idx++) {
    const row = raw[idx];
    const rowNum = idx + 2;
    const cellErr = (colIdx: number, msg: string): ParseError => ({
      cell: `${colLetter(colIdx)}${rowNum}`,
      message: msg,
    });
    const lineErr = (msg: string): ParseError => ({ cell: `Ligne ${rowNum}`, message: msg });

    const propfirm = row[COL.propfirm]?.trim();
    const nom_compte = row[COL.nom_compte]?.trim();
    const tiers = row[COL.tiers]?.trim();
    const taille_raw = row[COL.taille_compte]?.trim();
    const prix_raw = row[COL.prix_usd]?.trim();

    let hasError = false;

    if (!propfirm) { errors.push(cellErr(COL.propfirm, "valeur requise")); hasError = true; }
    if (!nom_compte) { errors.push(cellErr(COL.nom_compte, "valeur requise")); hasError = true; }
    if (!tiers) { errors.push(cellErr(COL.tiers, "valeur requise")); hasError = true; }

    if (!taille_raw) {
      errors.push(cellErr(COL.taille_compte, "valeur requise")); hasError = true;
    } else if (looksLikeDate(taille_raw)) {
      errors.push(cellErr(COL.taille_compte, `nombre attendu, date trouvée : "${taille_raw}"`)); hasError = true;
    } else if (!isPositiveNumber(taille_raw)) {
      errors.push(cellErr(COL.taille_compte, `nombre positif attendu, valeur trouvée : "${taille_raw}"`)); hasError = true;
    }

    if (!prix_raw) {
      errors.push(cellErr(COL.prix_usd, "valeur requise")); hasError = true;
    } else if (looksLikeDate(prix_raw)) {
      errors.push(cellErr(COL.prix_usd, `nombre attendu, date trouvée : "${prix_raw}"`)); hasError = true;
    } else if (!isPositiveNumber(prix_raw)) {
      errors.push(cellErr(COL.prix_usd, `nombre positif attendu, valeur trouvée : "${prix_raw}"`)); hasError = true;
    }

    const frais_raw = row[COL.frais_activation]?.trim();
    if (frais_raw) {
      if (looksLikeDate(frais_raw)) {
        errors.push(cellErr(COL.frais_activation, `nombre attendu, date trouvée : "${frais_raw}"`)); hasError = true;
      } else if (!isPositiveNumber(frais_raw)) {
        errors.push(cellErr(COL.frais_activation, `nombre positif attendu, valeur trouvée : "${frais_raw}"`)); hasError = true;
      }
    }

    // Colonnes optionnelles : un CSV historique sans ces colonnes reste valide.
    const date_achat_raw = row[COL.date_achat]?.trim();
    if (date_achat_raw && !isValidDate(date_achat_raw)) {
      errors.push(cellErr(COL.date_achat, `date YYYY-MM-DD attendue, valeur trouvée : "${date_achat_raw}"`)); hasError = true;
    }
    const date_cloture_raw = row[COL.date_cloture]?.trim();
    if (date_cloture_raw && !isValidDate(date_cloture_raw)) {
      errors.push(cellErr(COL.date_cloture, `date YYYY-MM-DD attendue, valeur trouvée : "${date_cloture_raw}"`)); hasError = true;
    }
    if (date_achat_raw && date_cloture_raw && date_cloture_raw < date_achat_raw) {
      errors.push(lineErr("date_cloture antérieure à date_achat")); hasError = true;
    }

    const payouts: ParsedPayout[] = [];
    for (let k = 1; k <= MAX_PAYOUTS; k++) {
      const dateRaw = row[payoutDateCol(k)]?.trim() ?? "";
      const amountRaw = row[payoutAmountCol(k)]?.trim() ?? "";
      if (!dateRaw && !amountRaw) continue;

      if (dateRaw && !amountRaw) {
        errors.push(lineErr(`date_payout_${k} renseignée mais montant_payout_${k} manquant`)); hasError = true; continue;
      }
      if (!dateRaw && amountRaw) {
        errors.push(lineErr(`montant_payout_${k} renseigné mais date_payout_${k} manquante`)); hasError = true; continue;
      }
      if (!isValidDate(dateRaw)) {
        errors.push(cellErr(payoutDateCol(k), `date YYYY-MM-DD attendue, valeur trouvée : "${dateRaw}"`)); hasError = true; continue;
      }
      if (looksLikeDate(amountRaw)) {
        errors.push(cellErr(payoutAmountCol(k), `nombre attendu, date trouvée : "${amountRaw}"`)); hasError = true; continue;
      }
      if (!isPositiveNumber(amountRaw)) {
        errors.push(cellErr(payoutAmountCol(k), `nombre positif attendu, valeur trouvée : "${amountRaw}"`)); hasError = true; continue;
      }
      payouts.push({ date: dateRaw, montant: parseFloat(amountRaw) });
    }

    if (hasError) continue;

    const nameKey = nom_compte.toLowerCase();
    if (seenNames.has(nameKey)) {
      errors.push(lineErr(`nom_compte "${nom_compte}" déjà présent en ligne ${seenNames.get(nameKey)}`));
      continue;
    }
    seenNames.set(nameKey, rowNum);

    const account: ParsedAccount = {
      rowNum,
      propfirm,
      nom_compte,
      tiers,
      taille_compte: parseFloat(taille_raw!),
      prix_usd: parseFloat(prix_raw!),
      groupe: row[COL.groupe]?.trim() || undefined,
      strategie: row[COL.strategie]?.trim() || undefined,
      frais_activation: frais_raw ? parseFloat(frais_raw) : undefined,
      date_achat: date_achat_raw || undefined,
      date_cloture: date_cloture_raw || undefined,
      payouts,
    };

    if (existingNames.has(nameKey)) {
      conflicts.push({ account, resolution: "skip" });
    } else {
      toCreate.push(account);
    }
  }

  return { toCreate, conflicts, errors };
}

// ── Component ──────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function PropFirmImportModal({ open, onOpenChange }: Props) {
  const t = useTranslation();
  const importFn = useAction(importPropFirmHistory);
  const { data: existingAccounts, isLoading: accountsLoading } = useQuery(getAccountsByUser);
  const { data: propFirmTemplates } = useQuery(getPropFirmTemplates);
  const { data: tradingStrategies } = useQuery(getTradingStrategiesByUser);

  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [conflicts, setConflicts] = useState<ConflictEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const existingNames = new Set<string>(
    (existingAccounts?.accounts ?? []).map((a: { name: string }) => a.name.toLowerCase().trim())
  );

  const handleFile = useCallback(
    (file: File) => {
      Papa.parse<string[]>(file, {
        header: false,
        skipEmptyLines: true,
        complete: (result) => {
          const [headerRow, ...dataRows] = result.data as string[][];
          // Mauvais formulaire (export de trades déposé ici, p. ex.) : inutile
          // de dérouler des dizaines d'erreurs de cellules, on oriente l'utilisateur.
          if (detectWrongCsvFormat(headerRow)) {
            setParseResult({ toCreate: [], conflicts: [], errors: [], wrongFormat: true });
            setConflicts([]);
            return;
          }
          const parsed = parseRows(upgradeLegacyRows(headerRow, dataRows), existingNames);
          setParseResult(parsed);
          setConflicts(parsed.conflicts);
        },
        error: () => {
          toast({ title: t.propFirmManager.importParseError, description: t.propFirmManager.importParseErrorDesc, variant: "destructive" });
        },
      });
    },
    [existingNames, t]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (accountsLoading) return;
      const file = e.dataTransfer.files[0];
      if (file?.name.endsWith(".csv")) handleFile(file);
    },
    [handleFile, accountsLoading]
  );

  const toggleConflictResolution = (idx: number) => {
    setConflicts((prev) =>
      prev.map((c, i) => i === idx ? { ...c, resolution: c.resolution === "skip" ? "update" : "skip" } : c)
    );
  };

  const totalPayouts =
    (parseResult?.toCreate ?? []).reduce((s, a) => s + a.payouts.length, 0) +
    conflicts.filter((c) => c.resolution !== "skip").reduce((s, c) => s + c.account.payouts.length, 0);

  const totalValidAccounts =
    (parseResult?.toCreate.length ?? 0) +
    conflicts.filter((c) => c.resolution === "update").length;

  const handleImport = async () => {
    if (!parseResult) return;

    const rows = [
      ...parseResult.toCreate.map((a) => ({ ...a, resolution: "create" as const })),
      ...conflicts.map((c) => ({ ...c.account, resolution: c.resolution })),
    ];
    if (rows.length === 0) return;

    setLoading(true);
    try {
      const result = await importFn({ rows });
      toast({
        title: t.propFirmManager.importSuccess,
        description: t.propFirmManager.importSuccessAccounts
          .replace("{created}", String(result.accountsCreated))
          .replace("{updated}", String(result.accountsUpdated))
          .replace("{skipped}", String(result.skipped))
          .replace("{payouts}", String(result.payoutsImported)),
      });
      onOpenChange(false);
      setParseResult(null);
      setConflicts([]);
    } catch {
      toast({ title: t.propFirmManager.importFailError, description: t.propFirmManager.importFailErrorDesc, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setParseResult(null); setConflicts([]); };

  // `wrongFormat` ne produit aucune erreur de cellule (on ne parse pas le
  // fichier) : sans ce test, l'import serait considéré comme sans erreur.
  const hasBlockingErrors =
    (parseResult?.errors.length ?? 0) > 0 || Boolean(parseResult?.wrongFormat);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!loading) { onOpenChange(o); if (!o) reset(); } }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t.propFirmManager.importModalTitle}</DialogTitle>
          <DialogDescription>{t.propFirmManager.importModalDesc}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <a
            href="/edgely-propfirm-template.csv"
            download
            className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
          >
            <Download className="h-4 w-4" />
            {t.propFirmManager.downloadTemplate}
          </a>

          <Accordion type="single" collapsible className="text-sm border rounded-lg">
            <AccordionItem value="reference" className="border-0">
              <AccordionTrigger className="px-3 py-2 text-xs font-medium hover:no-underline">
                {t.propFirmManager.importReferenceTitle}
              </AccordionTrigger>
              <AccordionContent className="px-3 pb-3">
                {/* Prop firms table */}
                {propFirmTemplates && (propFirmTemplates as any[]).length > 0 && (
                  <div className="overflow-x-auto mb-3">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-muted-foreground border-b">
                          <th className="text-left py-1 pr-3 font-medium">{t.propFirmManager.importRefPropFirm}</th>
                          <th className="text-left py-1 pr-3 font-medium">{t.propFirmManager.importRefAccountType}</th>
                          <th className="text-left py-1 font-medium">{t.propFirmManager.importReferenceSizes}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(propFirmTemplates as any[]).flatMap((firm: any) =>
                          firm.accountTypes.map((at: any, atIdx: number) => (
                            <tr key={`${firm.id}-${at.id}`} className="border-b border-border/40 last:border-0">
                              <td className="py-1 pr-3 align-top">
                                {atIdx === 0 ? (
                                  <span className="font-medium">{firm.name}</span>
                                ) : null}
                              </td>
                              <td className="py-1 pr-3 align-top text-muted-foreground">{at.name}</td>
                              <td className="py-1 align-top text-muted-foreground">
                                {at.tiers.map((tier: any) => tier.label).join(" · ")}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Strategies */}
                <div className="text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{t.propFirmManager.importRefStrategies} : </span>
                  {tradingStrategies && (tradingStrategies as any[]).length > 0
                    ? (tradingStrategies as any[]).map((s: any) => s.name).join(" · ")
                    : t.propFirmManager.importRefNoStrategy}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div
            role="button"
            tabIndex={0}
            aria-label={t.propFirmManager.importDropZone}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => { if (!accountsLoading) fileInputRef.current?.click(); }}
            onKeyDown={(e) => { if (!accountsLoading && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); fileInputRef.current?.click(); } }}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              accountsLoading
                ? "border-border opacity-50 cursor-wait"
                : isDragging
                ? "border-primary bg-primary/5 cursor-pointer"
                : "border-border hover:border-primary/50 cursor-pointer"
            }`}
          >
            <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{t.propFirmManager.importDropZone}</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ""; }}
            />
          </div>

          {parseResult?.wrongFormat && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {t.propFirmManager.importWrongFormatTitle}
              </p>
              <p className="text-xs text-muted-foreground">
                {t.propFirmManager.importWrongFormatDesc}
              </p>
              <Button variant="outline" size="sm" onClick={reset}>
                {t.propFirmManager.importFixFile}
              </Button>
            </div>
          )}

          {parseResult && !parseResult.wrongFormat && hasBlockingErrors && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-destructive flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {t.propFirmManager.importBlockedTitle}
              </p>
              <p className="text-xs text-muted-foreground">{t.propFirmManager.importBlockedDesc}</p>
              <ul className="text-xs text-destructive space-y-0.5 max-h-48 overflow-y-auto pl-5 list-disc">
                {parseResult.errors.map((e, i) => (
                  <li key={i}><span className="font-mono font-medium">{e.cell}</span> : {e.message}</li>
                ))}
              </ul>
              <Button variant="outline" size="sm" onClick={reset}>
                {t.propFirmManager.importFixFile}
              </Button>
            </div>
          )}

          {parseResult && !parseResult.wrongFormat && !hasBlockingErrors && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t.propFirmManager.importSummaryLine
                  .replace("{accounts}", String(parseResult.toCreate.length))
                  .replace("{conflicts}", String(conflicts.length))
                  .replace("{payouts}", String(totalPayouts))
                  .replace("{errors}", "0")}
              </p>

              {parseResult.toCreate.length > 0 && (
                <div className="space-y-1">
                  <p className="text-sm font-medium flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    {t.propFirmManager.importAccountsToCreate} ({parseResult.toCreate.length})
                  </p>
                  <ul className="text-xs text-muted-foreground pl-5 space-y-0.5">
                    {parseResult.toCreate.map((a, i) => (
                      <li key={i} className="flex items-center gap-1.5">
                        <span className="text-foreground">{a.nom_compte}</span>
                        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] leading-none">
                          {a.propfirm}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {conflicts.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium flex items-center gap-1">
                    <AlertCircle className="h-4 w-4 text-yellow-500" />
                    {t.propFirmManager.importConflicts} ({conflicts.length})
                  </p>
                  <ul className="space-y-2">
                    {conflicts.map((c, i) => (
                      <li key={`conflict_${i}_${c.account.nom_compte}`} className="flex items-center justify-between gap-2 text-xs border rounded px-3 py-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="font-medium truncate">{c.account.nom_compte}</span>
                          <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] leading-none text-muted-foreground">
                            {c.account.propfirm}
                          </span>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button
                            size="sm"
                            variant={c.resolution === "skip" ? "default" : "outline"}
                            className="h-6 text-xs px-2"
                            onClick={() => c.resolution !== "skip" && toggleConflictResolution(i)}
                          >
                            {t.propFirmManager.importConflictSkip}
                          </Button>
                          <Button
                            size="sm"
                            variant={c.resolution === "update" ? "default" : "outline"}
                            className="h-6 text-xs px-2"
                            onClick={() => c.resolution !== "update" && toggleConflictResolution(i)}
                          >
                            {t.propFirmManager.importConflictUpdate}
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {conflicts.some((c) => c.resolution === "skip") && (
                    <p className="text-xs text-muted-foreground italic">
                      {t.propFirmManager.importConflictEventsWarning}
                    </p>
                  )}
                </div>
              )}

              {totalPayouts > 0 && (
                <div className="space-y-1">
                  <p className="text-sm font-medium">
                    {t.propFirmManager.importPayoutsSection} ({totalPayouts})
                  </p>
                </div>
              )}

              {totalValidAccounts === 0 && (
                <p className="text-sm text-destructive">{t.propFirmManager.noValidRows}</p>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={loading}>
              {t.common.cancel}
            </Button>
            <Button
              size="sm"
              onClick={handleImport}
              disabled={loading || !parseResult || hasBlockingErrors || totalValidAccounts === 0}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t.propFirmManager.importBtn}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
